import type {Appareil} from "@mytour/shared";
import {NETTOYAGE_ACTIONS, NETTOYAGE_METHODES_ACCES} from "@mytour/shared";
import {useEffect, useMemo} from "react";
import type {Control, FieldErrors, UseFormSetValue, UseFormWatch} from "react-hook-form";
import {Controller} from "react-hook-form";
import {FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

export const cleaningSchema = z.object({
  nettoyageAction: z.string().min(1, "Requis"),
  nettoyageOutil: z.string().min(1, "Requis"),
  nettoyageMethodeAcces: z.array(z.string()).min(1, "Au moins une méthode d'accès"),
});

export type CleaningFormData = z.infer<typeof cleaningSchema>;

type Props = {
  control: Control<CleaningFormData>;
  errors: FieldErrors<CleaningFormData>;
  disabled: boolean;
  watch: UseFormWatch<CleaningFormData>;
  setValue: UseFormSetValue<CleaningFormData>;
};

export function CleaningForm({control, errors, disabled, watch, setValue}: Props) {
  const nettoyageAction = watch("nettoyageAction");

  const availableOutils = useMemo(() => {
    const found = NETTOYAGE_ACTIONS.find((a) => a.action === nettoyageAction);
    return found?.outils ?? [];
  }, [nettoyageAction]);

  useEffect(() => {
    const currentOutil = watch("nettoyageOutil");
    if (currentOutil && !availableOutils.includes(currentOutil)) {
      setValue("nettoyageOutil", "");
    }
  }, [availableOutils, setValue, watch]);

  return (
    <>
      <h5 style={{marginBottom: "16px"}}>Nettoyage</h5>

      <FormGroup floating>
        <Controller
          name="nettoyageAction"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-nettoyageAction"
              placeholder="Action de nettoyage"
              invalid={!!errors.nettoyageAction}
              disabled={disabled}
            >
              <option value="">--</option>
              {NETTOYAGE_ACTIONS.map((a) => (
                <option key={a.action} value={a.action}>
                  {a.action}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-nettoyageAction">Action de nettoyage</Label>
      </FormGroup>

      <FormGroup floating>
        <Controller
          name="nettoyageOutil"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-nettoyageOutil"
              placeholder="Outil"
              invalid={!!errors.nettoyageOutil}
              disabled={disabled || availableOutils.length === 0}
            >
              <option value="">--</option>
              {availableOutils.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-nettoyageOutil">Outil</Label>
      </FormGroup>

      <div style={{marginTop: "8px"}}>
        <strong>Méthode d&apos;accès</strong>
        {errors.nettoyageMethodeAcces && (
          <span style={{color: "red", marginLeft: "8px", fontSize: "12px"}}>
            {errors.nettoyageMethodeAcces.message}
          </span>
        )}
        <Controller
          name="nettoyageMethodeAcces"
          control={control}
          render={({field}) => (
            <>
              {NETTOYAGE_METHODES_ACCES.map((method) => (
                <FormGroup check key={method}>
                  <Input
                    type="checkbox"
                    id={`field-methode-${method}`}
                    checked={field.value.includes(method)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        field.onChange([...field.value, method]);
                      } else {
                        field.onChange(field.value.filter((v: string) => v !== method));
                      }
                    }}
                    disabled={disabled}
                  />
                  <Label check for={`field-methode-${method}`}>
                    {method}
                  </Label>
                </FormGroup>
              ))}
            </>
          )}
        />
      </div>
    </>
  );
}

export function getCleaningDefaultValues(appareil: Appareil): CleaningFormData {
  return {
    nettoyageAction: appareil.nettoyageAction,
    nettoyageOutil: appareil.nettoyageOutil,
    nettoyageMethodeAcces: appareil.nettoyageMethodeAcces,
  };
}
